import { createAuth0Instance } from '@/helpers/Auth/authClient';
import { AXIOS_HEADERS } from '@/types';
import { getAxios } from '@smg-real-estate/hg-networking';
import { Axios, AxiosHeaders, InternalAxiosRequestConfig } from 'axios';

export const createHeaders = (init?: Record<string, string>): AxiosHeaders => {
  const headers = new AxiosHeaders();
  if (init) headers.set(init);
  return headers;
};

const attachAuthToken = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  const auth0Instance = createAuth0Instance();
  if (!auth0Instance) {
    return config;
  }

  const { getAccessTokenSilently, isAuthenticated } = auth0Instance;
  if (!isAuthenticated.value) {
    return config;
  }

  const { id_token } = await getAccessTokenSilently({
    detailedResponse: true,
  });
  if (id_token) {
    config.headers.Authorization = `Bearer ${id_token}`;
  }

  return config;
};

export const getAxiosClient = (customHeaders?: AxiosHeaders): Axios => {
  const axios = getAxios();

  axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    if (customHeaders) {
      config.headers.set(customHeaders);
    }

    config.headers[AXIOS_HEADERS.API_KEY] = import.meta.env.VUE_APP_VALUATION_SERVICE_API_KEY ?? '';
    // x-e2e-test header was used to skip OTP SMS in property valuation service.
    // OTP steps have been removed for now, so this header is not needed.
    // config.headers[AXIOS_HEADERS.E2E_TEST_HEADER] = import.meta.env.VUE_APP_STAGE !== 'prod';

    return attachAuthToken(config);
  });

  return axios;
};

/**
 * For services other than property-valuation-service. Adds the Auth0 bearer token
 * only - no property-valuation API key, no custom headers - so those credentials
 * never leak to a different backend.
 */
export const getAccountManagementAxiosClient = (): Axios => {
  const axios = getAxios();

  axios.interceptors.request.use((config: InternalAxiosRequestConfig) => attachAuthToken(config));

  return axios;
};
